<template>
  <div class="mt-4">
    <!-- 고객사 리스트 -->
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark>
        <v-toolbar-title>단타의 신 · 고객사 관리</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" outlined small @click="openRegistDialog">
          <v-icon left small>mdi-plus</v-icon>고객사 등록
        </v-btn>
      </v-toolbar>
      <!-- 검색 영역 -->
      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="searchKeyword"
              label="고객사 검색"
              prepend-inner-icon="mdi-magnify"
              outlined dense hide-details clearable
              placeholder="아이디, 명칭, 사업자명"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text class="pa-0 pt-2">
        <div style="overflow-x: auto;">
        <v-data-table
          :headers="headers"
          :items="filteredClientList"
          :items-per-page="20"
          :footer-props="{'items-per-page-options':[20, 50, -1]}"
          class="elevation-0"
        >
          <template v-slot:item.client_name="{ item }">
            <span class="font-weight-bold">{{ item.client_name }}</span>
          </template>
          <template v-slot:item.currency="{ item }">
            <v-chip small :color="currencyColor(item.currency)" dark>{{ item.currency }}</v-chip>
          </template>
          <template v-slot:item.media_count="{ item }">
            <v-btn small outlined color="purple" @click="openMediaDialog(item)">
              <v-icon left x-small>mdi-television</v-icon>({{ item.media_count || 0 }}) 보기
            </v-btn>
          </template>
          <template v-slot:item.user_count="{ item }">
            <v-chip small outlined>{{ (item.users || []).length }}명</v-chip>
          </template>
          <template v-slot:item.created_at="{ item }">
            <span class="text-caption">{{ item.created_at || '-' }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openEditDialog(item)" title="수정">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small color="primary" @click="goClientPage(item)" title="고객사 페이지">
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        </div>
        <v-container v-if="clientList.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-domain</v-icon>
          <p class="text-body-1 grey--text mt-3">등록된 고객사가 없습니다.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- 고객사 등록/수정 다이얼로그 -->
    <v-dialog v-model="dialogEdit" max-width="860" scrollable>
      <v-card>
        <v-card-title class="pb-0">
          <v-icon left color="primary">{{ isEdit ? 'mdi-domain' : 'mdi-domain-plus' }}</v-icon>
          {{ isEdit ? (form.client_name || '고객사 수정') : '고객사 등록' }}
          <v-spacer></v-spacer>
          <v-chip v-if="form.client_id" small outlined class="ml-2">{{ form.client_id }}</v-chip>
        </v-card-title>
        <v-tabs v-model="editTab" color="primary" class="mt-1">
          <v-tab>
            <v-icon left small :color="tabStatus.basic ? 'green' : 'orange'">{{ tabStatus.basic ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
            기본정보
          </v-tab>
          <v-tab>
            <v-icon left small :color="tabStatus.settlement ? 'green' : 'orange'">{{ tabStatus.settlement ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
            정산정보
          </v-tab>
          <v-tab>
            <v-icon left small :color="tabStatus.account ? 'green' : 'orange'">{{ tabStatus.account ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
            계정/정산 담당자
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>
        <v-card-text class="pa-5" style="min-height: 400px;">
          <v-tabs-items v-model="editTab">
            <!-- 탭1: 기본정보 -->
            <v-tab-item>
              <v-row dense class="mt-2">
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.client_id" label="고객사 아이디 *" outlined dense hide-details class="mb-4" placeholder="영문/숫자 조합" :disabled="isEdit"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.client_name" label="고객사 명칭 *" outlined dense hide-details class="mb-4"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select v-model="form.currency" :items="currencyOptions" label="사용 통화" outlined dense hide-details class="mb-4"></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select v-model="form.timezone" :items="timezoneOptions" label="타임존" outlined dense hide-details class="mb-4"></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select v-model="form.biz_type" :items="bizTypeOptions" label="사업자 구분" outlined dense hide-details class="mb-4"></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.representative" label="대표자 명" outlined dense hide-details class="mb-4"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.biz_name" label="사업자 명" outlined dense hide-details class="mb-4"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.biz_number" label="사업자 번호" outlined dense hide-details class="mb-4"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.address" label="주소" outlined dense hide-details class="mb-4"></v-text-field>
                </v-col>
              </v-row>
              <v-divider class="mb-4"></v-divider>
              <div class="text-subtitle-2 font-weight-bold mb-3">
                <v-icon small left>mdi-key-variant</v-icon>키 관리
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.client_key" label="고객사 키" outlined dense hide-details class="mb-4" readonly append-icon="mdi-refresh" @click:append="generateClientKey"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.api_key" label="API KEY" outlined dense hide-details class="mb-4" readonly append-icon="mdi-refresh" @click:append="generateApiKey"></v-text-field>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- 탭2: 정산정보 -->
            <v-tab-item>
              <!-- 사업자 등록증 -->
              <div class="text-subtitle-2 font-weight-bold mb-3 mt-2">
                <v-icon small left>mdi-file-image</v-icon>사업자 등록증
              </div>
              <v-row dense align="center">
                <v-col cols="12" sm="6">
                  <v-file-input
                    v-model="form.biz_cert_file"
                    label="사업자 등록증 이미지"
                    outlined dense hide-details class="mb-2"
                    accept="image/*"
                    prepend-icon="mdi-camera"
                    @change="onBizCertFileChange"
                  ></v-file-input>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-btn
                    small depressed
                    :color="form.biz_cert_dt ? 'green' : 'orange'"
                    dark
                    @click="confirmBizCert"
                    :disabled="!form.biz_cert_preview"
                  >
                    <v-icon left small>{{ form.biz_cert_dt ? 'mdi-check-circle' : 'mdi-file-check' }}</v-icon>
                    {{ form.biz_cert_dt ? '확인 완료' : '확인 처리' }}
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="3">
                  <span v-if="form.biz_cert_dt" class="text-caption green--text">{{ form.biz_cert_dt }}</span>
                </v-col>
              </v-row>
              <v-img v-if="form.biz_cert_preview" :src="form.biz_cert_preview" max-height="180" contain class="mb-4 grey lighten-4 rounded"></v-img>

              <v-divider class="mb-4"></v-divider>

              <!-- 통장 사본 -->
              <div class="text-subtitle-2 font-weight-bold mb-3">
                <v-icon small left>mdi-file-image</v-icon>통장 사본
              </div>
              <v-row dense align="center">
                <v-col cols="12" sm="6">
                  <v-file-input
                    v-model="form.bank_cert_file"
                    label="통장 사본 이미지"
                    outlined dense hide-details class="mb-2"
                    accept="image/*"
                    prepend-icon="mdi-camera"
                    @change="onBankCertFileChange"
                  ></v-file-input>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-btn
                    small depressed
                    :color="form.bank_cert_dt ? 'green' : 'orange'"
                    dark
                    @click="confirmBankCert"
                    :disabled="!form.bank_cert_preview"
                  >
                    <v-icon left small>{{ form.bank_cert_dt ? 'mdi-check-circle' : 'mdi-file-check' }}</v-icon>
                    {{ form.bank_cert_dt ? '확인 완료' : '확인 처리' }}
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="3">
                  <span v-if="form.bank_cert_dt" class="text-caption green--text">{{ form.bank_cert_dt }}</span>
                </v-col>
              </v-row>
              <v-img v-if="form.bank_cert_preview" :src="form.bank_cert_preview" max-height="180" contain class="mb-4 grey lighten-4 rounded"></v-img>

              <v-divider class="mb-4"></v-divider>

              <!-- 정산 계좌 -->
              <div class="text-subtitle-2 font-weight-bold mb-3">
                <v-icon small left>mdi-bank</v-icon>정산 계좌
              </div>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="form.bank_name" label="거래 은행" outlined dense hide-details class="mb-4"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="form.account_holder" label="예금주" outlined dense hide-details class="mb-4"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="form.account_number" label="계좌번호" outlined dense hide-details class="mb-4"></v-text-field>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- 탭3: 계정/정산 담당자 -->
            <v-tab-item>
              <!-- 계정 사용자 -->
              <div class="d-flex align-center mb-3 mt-2">
                <v-icon small left>mdi-account-key</v-icon>
                <span class="text-subtitle-2 font-weight-bold">계정 사용자</span>
                <v-chip v-if="form.users && form.users.length === 0" x-small color="orange" dark class="ml-2">최소 1명 필수</v-chip>
                <v-spacer></v-spacer>
                <v-btn x-small color="primary" outlined @click="addUser">
                  <v-icon left x-small>mdi-plus</v-icon>추가
                </v-btn>
              </div>
              <v-simple-table v-if="form.users && form.users.length > 0" dense class="mb-4">
                <thead>
                  <tr>
                    <th>이메일</th>
                    <th style="width:120px">권한코드</th>
                    <th style="width:120px">역할코드</th>
                    <th style="width:50px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(user, idx) in form.users" :key="'u'+idx">
                    <td><v-text-field v-model="user.email" dense hide-details single-line class="body-2"></v-text-field></td>
                    <td><v-select v-model="user.auth_code" :items="authCodeOptions" dense hide-details single-line class="body-2"></v-select></td>
                    <td><v-select v-model="user.role_code" :items="roleCodeOptions" dense hide-details single-line class="body-2"></v-select></td>
                    <td><v-btn icon x-small @click="removeUser(idx)" :disabled="form.users.length <= 1"><v-icon x-small color="error">mdi-close</v-icon></v-btn></td>
                  </tr>
                </tbody>
              </v-simple-table>
              <p v-else class="text-body-2 orange--text mb-4">계정 사용자가 등록되어 있지 않습니다. 최소 1명을 추가해주세요.</p>

              <v-divider class="mb-4"></v-divider>

              <!-- 정산 담당자 -->
              <div class="d-flex align-center mb-3">
                <v-icon small left>mdi-card-account-phone</v-icon>
                <span class="text-subtitle-2 font-weight-bold">정산 담당자</span>
                <v-spacer></v-spacer>
                <v-btn x-small color="primary" outlined @click="addContact">
                  <v-icon left x-small>mdi-plus</v-icon>추가
                </v-btn>
              </div>
              <v-simple-table v-if="form.contacts && form.contacts.length > 0" dense class="mb-4">
                <thead>
                  <tr>
                    <th style="width:100px">담당업무</th>
                    <th>담당자명</th>
                    <th>전화번호</th>
                    <th>이메일</th>
                    <th style="width:70px">메일발송</th>
                    <th style="width:50px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, idx) in form.contacts" :key="'c'+idx">
                    <td><v-select v-model="c.duty" :items="dutyOptions" dense hide-details single-line class="body-2"></v-select></td>
                    <td><v-text-field v-model="c.name" dense hide-details single-line class="body-2"></v-text-field></td>
                    <td><v-text-field v-model="c.phone" dense hide-details single-line class="body-2"></v-text-field></td>
                    <td><v-text-field v-model="c.email" dense hide-details single-line class="body-2"></v-text-field></td>
                    <td class="text-center"><v-checkbox v-model="c.send_mail" dense hide-details class="mt-0 pt-0 d-inline-flex"></v-checkbox></td>
                    <td><v-btn icon x-small @click="removeContact(idx)"><v-icon x-small color="error">mdi-close</v-icon></v-btn></td>
                  </tr>
                </tbody>
              </v-simple-table>
              <p v-else class="text-body-2 grey--text">등록된 정산 담당자가 없습니다.</p>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialogEdit = false">취소</v-btn>
          <v-btn color="primary" depressed @click="isEdit ? saveEditClient() : saveNewClient()">
            <v-icon left small>mdi-check</v-icon>{{ isEdit ? '저장' : '등록' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 매체 리스트 다이얼로그 -->
    <v-dialog v-model="mediaDialog" max-width="700" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left color="purple">mdi-television</v-icon>
          {{ mediaDialogClientName }} · 매체 리스트
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-data-table
            v-if="mediaDialogList.length > 0"
            :headers="mediaHeaders"
            :items="mediaDialogList"
            hide-default-footer
            dense
            class="elevation-0"
          >
            <template v-slot:item.app_key="{ item }">
              <span style="font-family:monospace;" class="text-body-2">{{ item.app_key }}</span>
            </template>
            <template v-slot:item.is_active="{ item }">
              <v-chip x-small :color="item.is_active ? 'green' : 'grey'" dark>{{ item.is_active ? '활성' : '비활성' }}</v-chip>
            </template>
          </v-data-table>
          <v-container v-else class="text-center py-6">
            <p class="grey--text">등록된 매체가 없습니다.</p>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="mediaDialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="py-12"></div>
    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
export default {
  name: 'Danta_ClientList',
  data: () => ({
    dialogEdit: false,
    isEdit: false,
    editTab: 0,
    searchKeyword: '',
    mediaDialog: false,
    mediaDialogClientName: '',
    mediaDialogList: [],
    mediaHeaders: [
      { text: '매체 아이디', value: 'media_id', width: '120px' },
      { text: '매체 명칭', value: 'media_name', width: '150px' },
      { text: 'APP KEY', value: 'app_key', width: '180px' },
      { text: '매체 타입', value: 'media_type', width: '80px' },
      { text: '상태', value: 'is_active', width: '70px' },
    ],
    clientList: [],
    headers: [
      { text: '고객사 아이디', value: 'client_id', width: '110px' },
      { text: '고객사 명칭', value: 'client_name', width: '130px' },
      { text: '대표자', value: 'representative', width: '90px' },
      { text: '사업자 명', value: 'biz_name', width: '120px' },
      { text: '통화', value: 'currency', width: '70px' },
      { text: '매체', value: 'media_count', width: '70px' },
      { text: '사용자', value: 'user_count', width: '70px' },
      { text: '등록일시', value: 'created_at', width: '140px' },
      { text: '관리', value: 'actions', width: '100px', sortable: false },
    ],
    currencyOptions: ['KRW', 'JPY', 'USD'],
    timezoneOptions: ['Asia/Seoul', 'Asia/Tokyo', 'UTC', 'America/New_York', 'America/Los_Angeles'],
    bizTypeOptions: [
      { text: '법인사업자', value: 'corporation' },
      { text: '개인사업자', value: 'sole_proprietor' },
      { text: '개인', value: 'individual' },
    ],
    dutyOptions: [
      { text: '대표', value: 'representative' },
      { text: '정산', value: 'billing' },
      { text: '운영', value: 'operations' },
      { text: '마케팅', value: 'marketing' },
      { text: '기술', value: 'tech' },
    ],
    authCodeOptions: [
      { text: '사용자', value: 'user' },
      { text: '관리자', value: 'admin' },
    ],
    roleCodeOptions: [
      { text: '마케터', value: 'marketer' },
      { text: '운영자', value: 'operator' },
      { text: '뷰어', value: 'viewer' },
    ],
    form: {},
  }),
  computed: {
    filteredClientList() {
      if (!this.searchKeyword) return this.clientList;
      const kw = this.searchKeyword.toLowerCase();
      return this.clientList.filter(c =>
        (c.client_id || '').toLowerCase().includes(kw) ||
        (c.client_name || '').toLowerCase().includes(kw) ||
        (c.biz_name || '').toLowerCase().includes(kw)
      );
    },
    tabStatus() {
      const f = this.form;
      const basic = !!(f.client_id && f.client_name && f.client_key && f.api_key);
      const settlement = !!(f.biz_cert_dt && f.bank_cert_dt && f.bank_name && f.account_holder && f.account_number);
      const account = !!(f.users && f.users.length > 0 && f.users.every(u => u.email));
      return { basic, settlement, account };
    },
  },
  created() {
    this.resetForm();
    this.loadClientList();
  },
  methods: {
    ...mapActions([
      'DANTA_GET_CLIENT_LIST',
      'DANTA_CREATE_CLIENT',
      'DANTA_UPDATE_CLIENT',
      'DANTA_GET_CLIENT_USER_LIST',
      'DANTA_GET_CLIENT_CONTACT_LIST',
      'DANTA_SAVE_CLIENT_USERS',
      'DANTA_SAVE_CLIENT_CONTACTS',
      'DANTA_GET_MEDIA_LIST',
    ]),
    async loadClientList() {
      try {
        console.log('[DANTA] 고객사 리스트 요청 시작');
        const body_data = await this.DANTA_GET_CLIENT_LIST();
        console.log('[DANTA] 고객사 리스트 응답:', JSON.stringify(body_data));
        this.clientList = body_data.client_list || [];
      } catch (err) {
        console.error('[DANTA] 고객사 리스트 로드 실패:', err);
      }
    },
    resetForm() {
      this.form = {
        client_id: '', client_name: '', currency: 'KRW', timezone: 'KST',
        biz_type: '', representative: '', biz_name: '', biz_number: '', address: '',
        client_key: '', api_key: '',
        biz_cert_dt: '', biz_cert_file: null, biz_cert_preview: '',
        bank_cert_dt: '', bank_cert_file: null, bank_cert_preview: '',
        bank_name: '', account_holder: '', account_number: '',
        users: [{ email: '', auth_code: 'user', role_code: 'marketer' }],
        contacts: [],
        media_list: [], created_at: '',
      };
    },
    openRegistDialog() {
      this.isEdit = false;
      this.resetForm();
      this.generateClientKey();
      this.generateApiKey();
      this.editTab = 0;
      this.dialogEdit = true;
    },
    async openEditDialog(item) {
      this.isEdit = true;
      this.form = JSON.parse(JSON.stringify(item));
      this.form.biz_cert_preview = this.form.biz_cert_img || '';
      this.form.bank_cert_preview = this.form.bank_cert_img || '';
      this.form.biz_cert_file = null;
      this.form.bank_cert_file = null;
      // 계정 사용자 / 정산 담당자 로드
      try {
        const userData = await this.DANTA_GET_CLIENT_USER_LIST({ client_id: item.client_id });
        this.form.users = userData.user_list || [];
      } catch (e) { this.form.users = []; }
      try {
        const contactData = await this.DANTA_GET_CLIENT_CONTACT_LIST({ client_id: item.client_id });
        this.form.contacts = contactData.contact_list || [];
      } catch (e) { this.form.contacts = []; }
      this.editTab = 0;
      this.dialogEdit = true;
    },
    addUser() {
      this.form.users.push({ email: '', auth_code: 'user', role_code: 'marketer' });
    },
    removeUser(idx) {
      if (this.form.users.length <= 1) return;
      this.form.users.splice(idx, 1);
    },
    addContact() {
      this.form.contacts.push({ duty: 'billing', name: '', phone: '', email: '', send_mail: false });
    },
    removeContact(idx) {
      this.form.contacts.splice(idx, 1);
    },
    generateRandomKey(length) {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let key = '';
      for (let i = 0; i < length; i++) key += chars.charAt(Math.floor(Math.random() * chars.length));
      return key;
    },
    generateClientKey() {
      this.form.client_key = this.generateRandomKey(16);
    },
    generateApiKey() {
      this.form.api_key = this.generateRandomKey(32);
    },
    onBizCertFileChange(file) {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => { this.form.biz_cert_preview = e.target.result; };
        reader.readAsDataURL(file);
        this.form.biz_cert_dt = '';
      } else {
        this.form.biz_cert_preview = '';
        this.form.biz_cert_dt = '';
      }
    },
    onBankCertFileChange(file) {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => { this.form.bank_cert_preview = e.target.result; };
        reader.readAsDataURL(file);
        this.form.bank_cert_dt = '';
      } else {
        this.form.bank_cert_preview = '';
        this.form.bank_cert_dt = '';
      }
    },
    confirmBizCert() {
      this.form.biz_cert_dt = new Date().toLocaleString('ko-KR');
    },
    confirmBankCert() {
      this.form.bank_cert_dt = new Date().toLocaleString('ko-KR');
    },
    async saveNewClient() {
      if (!this.form.client_id || !this.form.client_name) {
        alert('고객사 아이디와 명칭은 필수입니다.');
        return;
      }
      if (!this.form.users || this.form.users.length === 0 || !this.form.users[0].email) {
        alert('계정 사용자를 최소 1명 등록해야 합니다.');
        this.editTab = 2;
        return;
      }
      if (this.clientList.some(c => c.client_id === this.form.client_id)) {
        alert('이미 존재하는 고객사 아이디입니다.');
        return;
      }
      try {
        const data = JSON.parse(JSON.stringify(this.form));
        // create_client 백엔드에서 users/contacts 함께 INSERT
        await this.DANTA_CREATE_CLIENT(data);
        this.dialogEdit = false;
        await this.loadClientList();
      } catch (err) {
        console.error(err);
        alert('고객사 등록에 실패했습니다.');
      }
    },
    async saveEditClient() {
      if (!this.form.client_name) {
        alert('고객사 명칭은 필수입니다.');
        return;
      }
      if (!this.form.users || this.form.users.length === 0 || !this.form.users[0].email) {
        alert('계정 사용자를 최소 1명 등록해야 합니다.');
        this.editTab = 2;
        return;
      }
      try {
        const data = JSON.parse(JSON.stringify(this.form));
        await this.DANTA_UPDATE_CLIENT(data);
        await this.DANTA_SAVE_CLIENT_USERS({ client_id: data.client_id, users: data.users || [] });
        await this.DANTA_SAVE_CLIENT_CONTACTS({ client_id: data.client_id, contacts: data.contacts || [] });
        this.dialogEdit = false;
        await this.loadClientList();
      } catch (err) {
        console.error(err);
        alert('고객사 수정에 실패했습니다.');
      }
    },
    async openMediaDialog(item) {
      this.mediaDialogClientName = item.client_name || item.client_id;
      this.mediaDialogList = [];
      this.mediaDialog = true;
      try {
        const body_data = await this.DANTA_GET_MEDIA_LIST({ client_id: item.client_id });
        this.mediaDialogList = body_data.media_list || [];
      } catch (err) {
        console.error('매체 리스트 로드 실패:', err);
      }
    },
    goClientPage(item) {
      // TODO: 고객사 상세 페이지 라우트 연결
      alert(`고객사 [${item.client_name}] 페이지로 이동 (라우트 연동 필요)`);
    },
    currencyColor(currency) {
      const map = { KRW: 'blue', JPY: 'red darken-1', USD: 'green darken-1' };
      return map[currency] || 'grey';
    },
  },
};
</script>
